import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, CheckCircle2Icon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { services } from '../data/services';
interface NearMeServiceProps {
  serviceSlug: string;
  onNavigate: (path: string) => void;
}
export function NearMeService({
  serviceSlug,
  onNavigate
}: NearMeServiceProps) {
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const service = services.find(s => s.slug === serviceSlug);
  if (!service) {
    return <div className="py-20 text-center" data-id="element-1030">Service not found</div>;
  }
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip) {
      onNavigate(`/${serviceSlug}/quote-form?zip=${zipCode}`);
    }
  };
  return <div className="min-h-screen bg-white font-sans" data-id="element-1031">
      {/* Hero Section */}
      <section className="bg-[#2563EB] pt-24 pb-32 relative overflow-hidden" data-id="element-1032">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-1033">
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" data-id="element-1034">
            <defs data-id="element-1035">
              <pattern id="grid-pattern" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-1036">
                <path d="M0 40L40 0H20L0 20M40 40V20L20 40" stroke="white" strokeWidth="2" fill="none" data-id="element-1037" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-pattern)" data-id="element-1038" />
          </svg>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1039">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight" data-id="element-1040">
            Top {service.name} Contractors Near Me
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-1041">
            Enter your ZIP Code to get matched with the top professionals near
            you.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="max-w-xl mx-auto bg-white p-2 rounded-2xl shadow-2xl flex flex-col sm:flex-row gap-2" data-id="element-1042">
            <div className="relative flex-1" data-id="element-1043">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-1044">
                <MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-1045" />
              </div>
              <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Enter ZIP Code" className="w-full h-14 pl-12 pr-4 text-lg font-medium bg-transparent border-none focus:ring-0 text-gray-900" maxLength={5} data-id="element-1046" />
            </div>
            <button onClick={handleContinue} disabled={!isValidZip} className={`h-14 px-8 rounded-xl font-bold text-lg text-white transition-all sm:w-auto w-full ${isValidZip ? 'bg-amber-500 hover:bg-amber-600 shadow-lg shadow-amber-500/30' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-1047">
              Start matching
            </button>
          </motion.div>
          <p className="text-sm text-blue-200 mt-4" data-id="element-1048">
            Free, no-obligation estimates.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-gray-100" data-id="element-1049">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center text-sm text-gray-500" data-id="element-1050">
          <button onClick={() => onNavigate('/')} className="hover:text-[#2563EB]" data-id="element-1051">
            Home
          </button>
          <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1052" />
          <span className="hover:text-[#2563EB] cursor-pointer" data-id="element-1053">Near Me</span>
          <ChevronRightIcon className="w-4 h-4 mx-2" data-id="element-1054" />
          <span className="text-gray-900 font-medium" data-id="element-1055">{service.name}</span>
        </div>
      </div>

      {/* Benefits Section */}
      <section className="py-20 bg-slate-50" data-id="element-1056">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1057">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-16" data-id="element-1058">
            {service.name} Made Simple, Quick, and Cost-Effective!
          </h2>

          <div className="flex flex-col md:flex-row items-center gap-12 lg:gap-24" data-id="element-1059">
            <div className="flex-1 space-y-6" data-id="element-1060">
              {service.benefits.map((benefit, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="flex items-start gap-4" data-id="element-1061">
                  <div className="mt-1 bg-green-100 rounded-full p-1 flex-shrink-0" data-id="element-1062">
                    <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-1063" />
                  </div>
                  <p className="text-lg font-bold text-slate-800" data-id="element-1064">{benefit}</p>
                </motion.div>)}
            </div>
            <div className="flex-1 w-full" data-id="element-1065">
              <img src="https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=800&q=80" alt={`${service.name} example`} className="rounded-3xl shadow-xl w-full object-cover h-[400px]" data-id="element-1066" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 bg-white" data-id="element-1067">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1068">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12" data-id="element-1069">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4" data-id="element-1070">
            {service.faqs.map((faq, idx) => <div key={idx} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-1071">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full px-6 py-5 text-left flex items-center justify-between focus:outline-none" data-id="element-1072">
                  <span className="font-bold text-slate-900 text-lg pr-8" data-id="element-1073">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform duration-200 flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-1074" />
                </button>
                {openFaq === idx && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1075">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}